// Vendor imports
const isArray = require('lodash/isArray')
const isRegExp = require('lodash/isRegExp')
const isString = require('lodash/isString')
const every = require('lodash/every')
const some = require('lodash/some')

let airbrake
// Allow this library to be loaded as a no-op on the server-side
if (typeof window === 'object') {
  // eslint-disable-next-line no-undef -- Defined by Webpack DefinePlugin
  const { enabled, environment, projectId, apiKey } = airbrakeConfig
  if (enabled) {
    const { Notifier } = require('@airbrake/browser')
    airbrake = new Notifier({
      environment,
      projectId,
      projectKey: apiKey
    })
    airbrake.addFilter(notice => {
      const [ err ] = notice.errors
      if (isIgnorableError(err)) {
        return null
      }
      return notice
    })
    window.airbrake = airbrake
  }
}

module.exports = {
  addFilter,
  attachBuildNumber,
  notify
}

async function notify(notice) {
  if (airbrake) {
    const result = await airbrake.notify(notice)
    if (result.id) {
      // eslint-disable-next-line no-console
      console.log('Airbrake notified:', result.id)
    } else {
      // eslint-disable-next-line no-console
      console.warn('Airbrake failed:', result.error)
    }
  } else {
    // eslint-disable-next-line no-console
    console.warn(`Airbrake is not enabled:`, notice)
  }
}

function addFilter(filter) {
  if (airbrake) {
    airbrake.addFilter(filter)
  }
}

function attachBuildNumber(buildNumber) {
  addFilter(notice => {
    notice.buildNumber = buildNumber
    return notice
  })
}

function isIgnorableError(err) {
  return some(IGNORABLE_ERROR_TESTS, test => test(err))
}

function isIgnorableValue(errorValue, testValue) {
  if (isArray(testValue)) {
    return some(testValue, i => isIgnorableValue(errorValue, i))
  }
  if (isRegExp(testValue)) {
    return testValue.test(errorValue)
  }
  if (isString(testValue)) {
    return testValue === errorValue
  }
  return false
}

const IGNORABLE_ERROR_TESTS = [
  // Errors in browser extensions (except our own)
  err => isIgnorableValue(err.backtrace[0].file, [
    /^chrome-extension:\/\/(?!jdgmgdpcphmdofoplgjifmdajnfojnin)/,
    /^moz-extension:\/\/(?!7217e373-875a-cc46-92ab-5b02a45c6e99)/,
    /^safari-extension:\/\/(?!com\.mixmedia\.mix\.mac\.app)/,
    /^javascript:/
  ]),
  // Known SecurityError caused by user-agent preferences
  err => every([
    isIgnorableValue(err.backtrace[0].file, [
      /^https:\/\/cdn\.branch\.io/,
      /^https:\/\/connect\.facebook\.net/
    ]),
    err.message === 'The operation is insecure.'
  ]),
  err => isIgnorableValue(err.message, [
    // Don't send notices that lack context
    'undefined',
    'Uncaught undefined',
    // Known non-actionable XHR errors
    'invalid csrf token',
    'Rate limit exceeded',
    // https://app.asana.com/0/102902769366319/690838729515894/f
    'XHR status[404] for url[https://suggestqueries.google.com/complete/search]',
    // Aborted requests are not really errors.
    /^XHR statusText\[abort\]/,
    // Unsent requests generally mean client-side network hiccups.
    /^XHR readyState\[UNSENT\]/,
    // Don't kick ourselves while we're down for 5xx errors on root-relative urls
    /^XHR status\[5[0-9]{2}\] for url\[\//
  ]),
  // Known third-party extension injected script errors
  err => every([
    err.backtrace[0].function === '',
    err.message === `can't redefine non-configurable property "userAgent"`
  ]),
  err => every([
    err.backtrace[0].file === '',
    isIgnorableValue(err.message, [
      ...([
        'tgetT',
        'ibPauseAllVideos',
        'getlistHotSearchCallback',
        'WeixinJSBridge',
        '__show__deepen'
      ].map(name => `${name} is not defined`)),
      ...([
        'checkDomStatus',
        'DOMNodeInsertedByJs',
        'end',
        '_avast_submit'
      ].map(name => `Cannot read property '${name}' of undefined`)),
      'Cannot redefine property: hidden',
      `Cannot set property 'install' of undefined`,
      `Failed to read the 'cssRules' property from 'CSSStyleSheet': Cannot access rules`,
      /^Blocked a frame with origin "[^"]+" from accessing/,
      /^[A-Za-z0-9]{4}\.data\.indexOf is not a function$/,
      /^vid_mate_\S+ is not defined$/
    ])
  ]),
  err => every([
    err.backtrace[0].function === 'global code',
    isIgnorableValue(err.message, [
      ...([
        'pktAnnotationHighlighter',
        'Ruby_ExtractLoginFormData',
        'article'
      ].map(name => `Can't find variable: ${name}`)),
      `null is not an object (evaluating 'article.element')`
    ])
  ])
]
